import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  getCaseStudies, getCaseStudy, createCaseStudy, updateCaseStudy, deleteCaseStudy,
  searchCallsForCaseStudy, uploadCallToCaseStudy, shareCaseStudy, revokeCaseStudyAccess,
  generateCaseStudyAnalysis, exportCaseStudy, getUsers
} from '@/lib/api';
import { useTheme } from '@/contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  FileSearch, Plus, Search, Upload, Share2, Download, Trash2, Edit, Eye, 
  AlertTriangle, CheckCircle, Clock, FileText, Users, Phone, Calendar,
  Loader2, X, ChevronRight, Scale, Shield, AlertCircle, Briefcase,
  Sparkles, RefreshCw, ExternalLink, Filter, Play
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const CASE_STUDY_TYPES = [
  { value: 'escalation', label: 'Escalation', icon: AlertTriangle, color: 'text-orange-500' },
  { value: 'violation', label: 'Violation', icon: AlertCircle, color: 'text-red-500' },
  { value: 'compliance', label: 'Compliance', icon: Shield, color: 'text-blue-500' },
  { value: 'process_breach', label: 'Process Breach', icon: Scale, color: 'text-purple-500' },
  { value: 'conduct_issue', label: 'Conduct Issue', icon: AlertTriangle, color: 'text-amber-500' },
  { value: 'quality_issue', label: 'Quality Issue', icon: FileSearch, color: 'text-yellow-500' },
  { value: 'positive_example', label: 'Positive Example', icon: CheckCircle, color: 'text-green-500' },
  { value: 'training', label: 'Training', icon: Briefcase, color: 'text-indigo-500' },
  { value: 'general', label: 'General', icon: FileText, color: 'text-gray-500' }
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-700' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-700' }
];

const STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-100 text-blue-700' },
  { value: 'review', label: 'Review', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-700' },
  { value: 'archived', label: 'Archived', color: 'bg-gray-200 text-gray-600' }
];

export default function CaseStudies() {
  const { tenantId, user } = useOutletContext();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { caseStudyId } = useParams();
  const isDark = theme === 'dark';

  // State
  const [caseStudies, setCaseStudies] = useState([]);
  const [currentCaseStudy, setCurrentCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showSearchCallsDialog, setShowSearchCallsDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);

  // Form state for new case study
  const [newCaseStudy, setNewCaseStudy] = useState({
    title: '',
    description: '',
    case_type: 'general',
    priority: 'medium',
    objective: '',
    expected_outcome: '',
    call_ids: [],
    tags: []
  });

  // Call search state
  const [callSearchQuery, setCallSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCalls, setSelectedCalls] = useState([]);
  const [searchingCalls, setSearchingCalls] = useState(false);

  // Users for sharing
  const [users, setUsers] = useState([]);
  const [selectedShareUsers, setSelectedShareUsers] = useState([]);
  const [shareMessage, setShareMessage] = useState('');

  // Processing
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Fetch case studies
  const fetchCaseStudies = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter && typeFilter !== 'all') params.case_type = typeFilter;

      const response = await getCaseStudies(tenantId, user.id, params);
      setCaseStudies(response.data.case_studies || []);
    } catch (error) {
      console.error('Error fetching case studies:', error);
      toast.error('Failed to load case studies');
    } finally {
      setLoading(false);
    }
  };

  // Fetch single case study
  const fetchCaseStudy = async (id) => {
    try {
      const response = await getCaseStudy(id, user.id);
      setCurrentCaseStudy(response.data);
    } catch (error) {
      console.error('Error fetching case study:', error);
      toast.error('Failed to load case study');
    }
  };

  // Fetch users for sharing
  const fetchUsers = async () => {
    try {
      const response = await getUsers(tenantId);
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    if (tenantId && user?.id) {
      fetchCaseStudies();
      fetchUsers();
    }
  }, [tenantId, user?.id, searchQuery, statusFilter, typeFilter]);

  useEffect(() => {
    if (caseStudyId) {
      fetchCaseStudy(caseStudyId);
    } else {
      setCurrentCaseStudy(null);
    }
  }, [caseStudyId]);

  // Create case study
  const handleCreateCaseStudy = async () => {
    if (!newCaseStudy.title || !newCaseStudy.objective) {
      toast.error('Please provide a title and objective');
      return;
    }

    try {
      const data = {
        ...newCaseStudy,
        call_ids: selectedCalls.map(c => c.id)
      };
      const response = await createCaseStudy(tenantId, user.id, data);
      toast.success('Case study created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchCaseStudies();
      
      // Navigate to the new case study
      if (response.data.case_study?.id) {
        navigate(`/dashboard/case-studies/${response.data.case_study.id}`);
      }
    } catch (error) {
      console.error('Error creating case study:', error);
      toast.error('Failed to create case study');
    }
  };

  const resetForm = () => {
    setNewCaseStudy({
      title: '',
      description: '',
      case_type: 'general',
      priority: 'medium',
      objective: '',
      expected_outcome: '',
      call_ids: [],
      tags: []
    });
    setSelectedCalls([]);
  };

  // Search calls
  const handleSearchCalls = async () => {
    if (!callSearchQuery.trim()) {
      toast.error('Please enter a phone number or search term');
      return;
    }

    try {
      setSearchingCalls(true);
      const response = await searchCallsForCaseStudy(tenantId, { phone_number: callSearchQuery });
      setSearchResults(response.data.calls || []);
    } catch (error) {
      console.error('Error searching calls:', error);
      toast.error('Failed to search calls');
    } finally {
      setSearchingCalls(false);
    }
  };

  // Toggle call selection
  const toggleCallSelection = (call) => {
    setSelectedCalls(prev => {
      const exists = prev.find(c => c.id === call.id);
      if (exists) {
        return prev.filter(c => c.id !== call.id);
      } else {
        return [...prev, call];
      }
    });
  };

  // Add selected calls to case study
  const handleAddCallsToCaseStudy = () => {
    setShowSearchCallsDialog(false);
    toast.success(`${selectedCalls.length} calls added`);
  };

  // Generate analysis
  const handleGenerateAnalysis = async () => {
    if (!currentCaseStudy) return;

    try {
      setGenerating(true);
      await generateCaseStudyAnalysis(currentCaseStudy.id, user.id);
      toast.success('Analysis generation started. This may take a few minutes.');
      
      // Poll for updates
      const pollInterval = setInterval(async () => {
        const response = await getCaseStudy(currentCaseStudy.id, user.id);
        setCurrentCaseStudy(response.data);
        if (response.data.status === 'completed' || response.data.generation_error) {
          clearInterval(pollInterval);
          setGenerating(false);
          if (response.data.status === 'completed') {
            toast.success('Analysis generated successfully!');
          }
        }
      }, 5000);

      // Stop polling after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        setGenerating(false);
      }, 300000);

    } catch (error) {
      console.error('Error generating analysis:', error);
      toast.error('Failed to start analysis generation');
      setGenerating(false);
    }
  };

  // Export case study
  const handleExport = async (format) => {
    if (!currentCaseStudy) return;

    try {
      setExporting(true);
      const response = await exportCaseStudy(currentCaseStudy.id, user.id, format);
      const { content, filename, encoding } = response.data;

      let blob;
      if (format === 'pdf' && encoding === 'base64') {
        // Decode base64 PDF content
        const binaryString = atob(content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        blob = new Blob([bytes], { type: 'application/pdf' });
      } else {
        // Text-based formats (markdown, html)
        const mimeType = format === 'html' ? 'text/html' : 'text/markdown';
        blob = new Blob([content], { type: mimeType });
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`Case study exported as ${format.toUpperCase()}`);
      setShowExportDialog(false);
    } catch (error) {
      console.error('Error exporting case study:', error);
      toast.error('Failed to export case study');
    } finally {
      setExporting(false);
    }
  };

  // Share case study
  const handleShare = async () => {
    if (!currentCaseStudy || selectedShareUsers.length === 0) return;

    try {
      await shareCaseStudy(currentCaseStudy.id, user.id, {
        user_ids: selectedShareUsers,
        message: shareMessage
      });
      toast.success(`Case study shared with ${selectedShareUsers.length} users`);
      setShowShareDialog(false);
      setSelectedShareUsers([]);
      setShareMessage('');
      fetchCaseStudy(currentCaseStudy.id);
    } catch (error) {
      console.error('Error sharing case study:', error);
      toast.error('Failed to share case study');
    }
  };

  // Revoke access
  const handleRevokeAccess = async (targetUserId) => {
    if (!currentCaseStudy) return;

    try {
      await revokeCaseStudyAccess(currentCaseStudy.id, targetUserId, user.id);
      toast.success('Access revoked');
      fetchCaseStudy(currentCaseStudy.id);
    } catch (error) {
      console.error('Error revoking access:', error);
      toast.error('Failed to revoke access');
    }
  };

  // Delete case study
  const handleDelete = async () => {
    if (!currentCaseStudy) return;
    if (!window.confirm('Are you sure you want to delete this case study?')) return;

    try {
      await deleteCaseStudy(currentCaseStudy.id, user.id);
      toast.success('Case study deleted');
      navigate('/dashboard/case-studies');
      fetchCaseStudies();
    } catch (error) {
      console.error('Error deleting case study:', error);
      toast.error('Failed to delete case study');
    }
  };

  // Get type info
  const getTypeInfo = (type) => CASE_STUDY_TYPES.find(t => t.value === type) || CASE_STUDY_TYPES[8];
  const getPriorityInfo = (priority) => PRIORITIES.find(p => p.value === priority) || PRIORITIES[1];
  const getStatusInfo = (status) => STATUSES.find(s => s.value === status) || STATUSES[0];

  // Render case study list
  const renderCaseStudyList = () => (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Case Studies
          </h1>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Investigation & Compliance Case Management
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-case-study-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Case Study
        </Button>
      </div>

      {/* Filters */}
      <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search case studies..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`pl-10 ${isDark ? 'bg-gray-700 border-gray-600' : ''}`}
                  data-testid="case-study-search"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className={`w-[150px] ${isDark ? 'bg-gray-700 border-gray-600' : ''}`}>
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {STATUSES.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className={`w-[180px] ${isDark ? 'bg-gray-700 border-gray-600' : ''}`}>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {CASE_STUDY_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Case Studies Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : caseStudies.length === 0 ? (
        <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
          <CardContent className="py-12 text-center">
            <FileSearch className={`h-12 w-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
              No case studies found. Create your first case study to get started.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {caseStudies.map(cs => {
            const typeInfo = getTypeInfo(cs.case_type);
            const TypeIcon = typeInfo.icon;
            const priorityInfo = getPriorityInfo(cs.priority);
            const statusInfo = getStatusInfo(cs.status);

            return (
              <Card 
                key={cs.id} 
                className={`cursor-pointer transition-all hover:shadow-lg ${isDark ? 'bg-gray-800 border-gray-700 hover:border-gray-600' : 'hover:border-gray-300'}`}
                onClick={() => navigate(`/dashboard/case-studies/${cs.id}`)}
                data-testid={`case-study-card-${cs.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <TypeIcon className={`h-5 w-5 ${typeInfo.color}`} />
                      <Badge className={priorityInfo.color}>{priorityInfo.label}</Badge>
                    </div>
                    <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                  </div>
                  <CardTitle className={`text-lg mt-2 ${isDark ? 'text-white' : ''}`}>
                    {cs.title}
                  </CardTitle>
                  <CardDescription className={isDark ? 'text-gray-400' : ''}>
                    {cs.description || cs.objective?.substring(0, 100)}...
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className={`flex items-center justify-between text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    <span className="flex items-center gap-1">
                      <Phone className="h-4 w-4" />
                      {cs.call_count || 0} calls
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {new Date(cs.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    By {cs.creator_name}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );

  // Render case study detail
  const renderCaseStudyDetail = () => {
    if (!currentCaseStudy) return null;

    const typeInfo = getTypeInfo(currentCaseStudy.case_type);
    const TypeIcon = typeInfo.icon;
    const priorityInfo = getPriorityInfo(currentCaseStudy.priority);
    const statusInfo = getStatusInfo(currentCaseStudy.status);
    const stats = currentCaseStudy.statistics || {};

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/dashboard/case-studies')}
              className="mb-2"
            >
              ← Back to Case Studies
            </Button>
            <div className="flex items-center gap-3">
              <TypeIcon className={`h-8 w-8 ${typeInfo.color}`} />
              <div>
                <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {currentCaseStudy.title}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={priorityInfo.color}>{priorityInfo.label}</Badge>
                  <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                  <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    by {currentCaseStudy.creator_name}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowShareDialog(true)}
              data-testid="share-case-study-btn"
            >
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowExportDialog(true)}
              data-testid="export-case-study-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button 
              variant="destructive" 
              size="sm" 
              onClick={handleDelete}
              data-testid="delete-case-study-btn"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
            <CardContent className="p-4">
              <div className={`text-2xl font-bold ${isDark ? 'text-white' : ''}`}>
                {stats.total_calls || currentCaseStudy.call_ids?.length || 0}
              </div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Calls</div>
            </CardContent>
          </Card>
          <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
            <CardContent className="p-4">
              <div className={`text-2xl font-bold ${isDark ? 'text-white' : ''}`}>
                {stats.total_duration_seconds ? Math.round(stats.total_duration_seconds / 60) : 0} min
              </div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Duration</div>
            </CardContent>
          </Card>
          <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
            <CardContent className="p-4">
              <div className={`text-2xl font-bold ${isDark ? 'text-white' : ''}`}>
                {stats.average_score || 0}/100
              </div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Avg Score</div>
            </CardContent>
          </Card>
          <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
            <CardContent className="p-4">
              <div className={`text-2xl font-bold ${isDark ? 'text-white' : ''}`}>
                {stats.unique_users || 0}
              </div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Users Involved</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className={isDark ? 'bg-gray-800' : ''}>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="calls">Calls ({currentCaseStudy.calls?.length || 0})</TabsTrigger>
            <TabsTrigger value="analysis">Analysis</TabsTrigger>
            <TabsTrigger value="sharing">Sharing</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4 mt-4">
            <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
              <CardHeader>
                <CardTitle className={isDark ? 'text-white' : ''}>Objective</CardTitle>
              </CardHeader>
              <CardContent>
                <p className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                  {currentCaseStudy.objective}
                </p>
              </CardContent>
            </Card>

            {currentCaseStudy.expected_outcome && (
              <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
                <CardHeader>
                  <CardTitle className={isDark ? 'text-white' : ''}>Expected Outcome</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                    {currentCaseStudy.expected_outcome}
                  </p>
                </CardContent>
              </Card>
            )}

            {currentCaseStudy.description && (
              <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
                <CardHeader>
                  <CardTitle className={isDark ? 'text-white' : ''}>Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                    {currentCaseStudy.description}
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="calls" className="space-y-4 mt-4">
            <div className="flex justify-between items-center">
              <h3 className={`font-semibold ${isDark ? 'text-white' : ''}`}>Included Calls</h3>
              <Button 
                size="sm" 
                onClick={() => setShowSearchCallsDialog(true)}
                data-testid="add-calls-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Calls
              </Button>
            </div>

            {currentCaseStudy.calls?.length > 0 ? (
              <div className="space-y-2">
                {currentCaseStudy.calls.map(call => (
                  <Card key={call.id} className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`font-medium ${isDark ? 'text-white' : ''}`}>{call.file_name}</p>
                          <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            <span>{call.user_name || 'Unknown'}</span>
                            <span className="mx-2">•</span>
                            <span>{call.candidate_name || 'Unknown'}</span>
                            <span className="mx-2">•</span>
                            <span>{Math.round((call.duration || 0) / 60)} min</span>
                            <span className="mx-2">•</span>
                            <span>Score: {call.average_score || 0}</span>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => window.open(`/dashboard/calls/${call.id}`, '_blank')}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
                <CardContent className="py-8 text-center">
                  <Phone className={`h-8 w-8 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                  <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                    No calls added yet. Search and add calls to this case study.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="analysis" className="space-y-4 mt-4">
            <div className="flex justify-between items-center">
              <h3 className={`font-semibold ${isDark ? 'text-white' : ''}`}>AI Analysis</h3>
              <Button 
                onClick={handleGenerateAnalysis} 
                disabled={generating || !currentCaseStudy.call_ids?.length}
                data-testid="generate-analysis-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Analysis
                  </>
                )}
              </Button>
            </div>

            {currentCaseStudy.analysis ? (
              <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
                <CardContent className="p-6">
                  <div className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}>
                    <ReactMarkdown>{currentCaseStudy.analysis}</ReactMarkdown>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
                <CardContent className="py-12 text-center">
                  <Sparkles className={`h-8 w-8 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                  <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                    {currentCaseStudy.call_ids?.length > 0 
                      ? 'Click "Generate Analysis" to create an AI-powered investigation report.'
                      : 'Add calls to this case study first, then generate the analysis.'}
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="sharing" className="space-y-4 mt-4">
            <div className="flex justify-between items-center">
              <h3 className={`font-semibold ${isDark ? 'text-white' : ''}`}>Shared With</h3>
              <Button size="sm" onClick={() => setShowShareDialog(true)}>
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
            </div>

            {currentCaseStudy.shared_with?.filter(s => !s.access_revoked).length > 0 ? (
              <div className="space-y-2">
                {currentCaseStudy.shared_with.filter(s => !s.access_revoked).map((share, idx) => (
                  <Card key={idx} className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`font-medium ${isDark ? 'text-white' : ''}`}>{share.user_name}</p>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            Shared on {new Date(share.shared_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleRevokeAccess(share.user_id)}
                        >
                          <X className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
                <CardContent className="py-8 text-center">
                  <Users className={`h-8 w-8 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                  <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                    This case study hasn't been shared with anyone yet.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    );
  };

  return (
    <div className={`p-6 ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50'}`}>
      {caseStudyId ? renderCaseStudyDetail() : renderCaseStudyList()}

      {/* Create Case Study Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className={`max-w-2xl ${isDark ? 'bg-gray-800 border-gray-700' : ''}`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>Create New Case Study</DialogTitle>
            <DialogDescription>
              Create an investigation case study for escalations, violations, or compliance issues.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={isDark ? 'text-gray-300' : ''}>Title *</Label>
                <Input
                  value={newCaseStudy.title}
                  onChange={(e) => setNewCaseStudy({...newCaseStudy, title: e.target.value})}
                  placeholder="e.g., Compliance Issue - User X"
                  className={isDark ? 'bg-gray-700 border-gray-600' : ''}
                  data-testid="case-study-title-input"
                />
              </div>
              <div>
                <Label className={isDark ? 'text-gray-300' : ''}>Type</Label>
                <Select 
                  value={newCaseStudy.case_type} 
                  onValueChange={(v) => setNewCaseStudy({...newCaseStudy, case_type: v})}
                >
                  <SelectTrigger className={isDark ? 'bg-gray-700 border-gray-600' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CASE_STUDY_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={isDark ? 'text-gray-300' : ''}>Priority</Label>
                <Select 
                  value={newCaseStudy.priority} 
                  onValueChange={(v) => setNewCaseStudy({...newCaseStudy, priority: v})}
                >
                  <SelectTrigger className={isDark ? 'bg-gray-700 border-gray-600' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={isDark ? 'text-gray-300' : ''}>Tags (comma separated)</Label>
                <Input
                  value={newCaseStudy.tags.join(', ')}
                  onChange={(e) => setNewCaseStudy({...newCaseStudy, tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)})}
                  placeholder="compliance, urgent, hr"
                  className={isDark ? 'bg-gray-700 border-gray-600' : ''}
                />
              </div>
            </div>

            <div>
              <Label className={isDark ? 'text-gray-300' : ''}>Objective - What are you looking for? *</Label>
              <Textarea
                value={newCaseStudy.objective}
                onChange={(e) => setNewCaseStudy({...newCaseStudy, objective: e.target.value})}
                placeholder="Describe what you want to investigate and find from this case study..."
                rows={3}
                className={isDark ? 'bg-gray-700 border-gray-600' : ''}
                data-testid="case-study-objective-input"
              />
            </div>

            <div>
              <Label className={isDark ? 'text-gray-300' : ''}>Expected Outcome</Label>
              <Textarea
                value={newCaseStudy.expected_outcome}
                onChange={(e) => setNewCaseStudy({...newCaseStudy, expected_outcome: e.target.value})}
                placeholder="What outcome are you expecting from this investigation?"
                rows={2}
                className={isDark ? 'bg-gray-700 border-gray-600' : ''}
              />
            </div>

            <div>
              <Label className={isDark ? 'text-gray-300' : ''}>Description</Label>
              <Textarea
                value={newCaseStudy.description}
                onChange={(e) => setNewCaseStudy({...newCaseStudy, description: e.target.value})}
                placeholder="Additional details about this case..."
                rows={2}
                className={isDark ? 'bg-gray-700 border-gray-600' : ''}
              />
            </div>

            {/* Selected Calls */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className={isDark ? 'text-gray-300' : ''}>Calls ({selectedCalls.length})</Label>
                <Button size="sm" variant="outline" onClick={() => setShowSearchCallsDialog(true)}>
                  <Search className="h-4 w-4 mr-2" />
                  Search Calls
                </Button>
              </div>
              {selectedCalls.length > 0 ? (
                <div className="space-y-1 max-h-[150px] overflow-y-auto">
                  {selectedCalls.map(call => (
                    <div key={call.id} className={`flex items-center justify-between p-2 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <span className={`text-sm ${isDark ? 'text-gray-300' : ''}`}>{call.file_name}</span>
                      <Button size="sm" variant="ghost" onClick={() => toggleCallSelection(call)}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  No calls selected. Search and add calls to this case study.
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreateCaseStudy} data-testid="create-case-study-submit">
              Create Case Study
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Search Calls Dialog */}
      <Dialog open={showSearchCallsDialog} onOpenChange={setShowSearchCallsDialog}>
        <DialogContent className={`max-w-2xl ${isDark ? 'bg-gray-800 border-gray-700' : ''}`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>Search Calls</DialogTitle>
            <DialogDescription>
              Search by phone number or upload calls manually.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Input
                value={callSearchQuery}
                onChange={(e) => setCallSearchQuery(e.target.value)}
                placeholder="Enter phone number..."
                className={isDark ? 'bg-gray-700 border-gray-600' : ''}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchCalls()}
                data-testid="call-search-input"
              />
              <Button onClick={handleSearchCalls} disabled={searchingCalls}>
                {searchingCalls ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>

            {searchResults.length > 0 && (
              <ScrollArea className="h-[300px]">
                <div className="space-y-2">
                  {searchResults.map(call => {
                    const isSelected = selectedCalls.find(c => c.id === call.id);
                    return (
                      <div 
                        key={call.id}
                        className={`p-3 rounded border cursor-pointer transition-all ${
                          isSelected 
                            ? 'border-blue-500 bg-blue-500/10' 
                            : isDark ? 'border-gray-700 hover:border-gray-600' : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => toggleCallSelection(call)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={`font-medium ${isDark ? 'text-white' : ''}`}>{call.file_name}</p>
                            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              {call.user_name || 'Unknown'} • {call.candidate_name || 'Unknown'} • Score: {call.average_score || 0}
                            </p>
                          </div>
                          <Checkbox checked={!!isSelected} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSearchCallsDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddCallsToCaseStudy}>
              Add {selectedCalls.length} Calls
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>Share Case Study</DialogTitle>
            <DialogDescription>
              Select users to share this case study with.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <ScrollArea className="h-[200px]">
              <div className="space-y-2">
                {users.filter(u => u.id !== user?.id).map(u => (
                  <div 
                    key={u.id}
                    className={`p-3 rounded border cursor-pointer ${
                      selectedShareUsers.includes(u.id)
                        ? 'border-blue-500 bg-blue-500/10'
                        : isDark ? 'border-gray-700' : 'border-gray-200'
                    }`}
                    onClick={() => {
                      setSelectedShareUsers(prev => 
                        prev.includes(u.id) 
                          ? prev.filter(id => id !== u.id)
                          : [...prev, u.id]
                      );
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={isDark ? 'text-white' : ''}>{u.name}</p>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{u.email}</p>
                      </div>
                      <Checkbox checked={selectedShareUsers.includes(u.id)} />
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div>
              <Label className={isDark ? 'text-gray-300' : ''}>Message (optional)</Label>
              <Textarea
                value={shareMessage}
                onChange={(e) => setShareMessage(e.target.value)}
                placeholder="Add a message..."
                rows={2}
                className={isDark ? 'bg-gray-700 border-gray-600' : ''}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShareDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleShare} disabled={selectedShareUsers.length === 0}>
              Share with {selectedShareUsers.length} Users
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>Export Case Study</DialogTitle>
            <DialogDescription>
              Choose the format for your export.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-3 gap-4 py-4">
            <Button 
              variant="outline" 
              className="flex flex-col items-center p-6 h-auto"
              onClick={() => handleExport('markdown')}
              disabled={exporting}
            >
              <FileText className="h-8 w-8 mb-2" />
              <span>Markdown</span>
            </Button>
            <Button 
              variant="outline" 
              className="flex flex-col items-center p-6 h-auto"
              onClick={() => handleExport('html')}
              disabled={exporting}
            >
              <FileText className="h-8 w-8 mb-2" />
              <span>HTML</span>
            </Button>
            <Button 
              variant="outline" 
              className="flex flex-col items-center p-6 h-auto"
              onClick={() => handleExport('pdf')}
              disabled={exporting}
            >
              <FileText className="h-8 w-8 mb-2" />
              <span>PDF</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
